import { useState, useCallback } from "react";
import {
  getGeneratedCodes,
  type GeneratedCode,
} from "@/services/generatedCodeService";
import type {
  GeneratedCodeEntity,
  GeneratedCodeFilters,
  UseGeneratedCodesReturn,
} from "@/types/generatedCode";
import { extractErrorMessage } from "@/types/generatedCode";

/**
 * Hook personnalisé pour gérer le chargement des codes générés
 * Centralise la logique de chargement et la gestion d'état
 */
export const useGeneratedCodes = (): UseGeneratedCodesReturn => {
  const [codes, setCodes] = useState<GeneratedCodeEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Charge les codes générés avec les filtres optionnels
   */
  const loadCodes = useCallback(
    async (filters?: GeneratedCodeFilters, errorMessage?: string) => {
      try {
        setIsLoading(true);
        setError(null);

        const data: GeneratedCode[] = await getGeneratedCodes(filters);
        setCodes(data);
      } catch (err: unknown) {
        console.error("❌ Error loading generated codes:", err);

        // Utiliser la fonction helper pour extraire le message
        const finalErrorMessage = extractErrorMessage(
          err,
          errorMessage || "Failed to load codes",
        );

        setError(finalErrorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  /**
   * Recharge les codes (pour rafraîchir après une modification)
   */
  const reloadCodes = useCallback(
    (filters?: GeneratedCodeFilters) => {
      return loadCodes(filters);
    },
    [loadCodes],
  );

  /**
   * Réinitialise l'état d'erreur
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // État
    codes,
    isLoading,
    error,

    // Actions
    loadCodes,
    reloadCodes,
    clearError,

    // Setter direct si nécessaire (pour les optimisations)
    setCodes,
  };
};
